import { Component, OnInit, Input } from '@angular/core';
import { NgModel } from '@angular/forms';

import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { TreeNode } from 'primeng/api';
import { List } from 'linqts-es-module';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { FinancialAdjustmentFilter } from '@models/financialAdjustmentfilter.model';
import { Study } from '../../../models/study.model';
import { Ratio } from '../../../models/ratio.model';
import { VendorAccountMapping } from '../../../models/vendorAccountMapping.model';
import { ConfirmationResult, ConfirmationMessage, AcknowledgeMessage } from '../../../shared/_constants';

import { MasterMatrixService } from '../../../services/mastermatrix.service';
import { StudyService } from '../../../services/study.service';

import { LookupService } from '@services/lookup.service';
import { StudyDetailService } from '@services/study-detail.service';
import { LoggingService } from '@services/logging.service';
import { TreeViewService } from '@services/treeview.service';
import { DialogService } from '@services/dialog.service';
import { RatioVendorAccountMapping } from '../../../models/ratioVendorAccountMapping.model';
import { AveragingMethodId, BasePLIVendorAccount } from '../../_study_constants';
import { Report } from '../../../models/reportTypeEnum.model';
import _ from 'lodash-es';

@Component({
  selector: 'app-financial-adjustments-export-modal',
  templateUrl: './financial-adjustments-export-modal.component.html',
  styleUrls: ['./financial-adjustments-export-modal.component.css'],
})
export class FinancialAdjustmentsExportModalComponent implements OnInit {
  @Input() isExportFromSearchstrategy = false;

  readOnly: boolean;
  exportAll: boolean;
  study: Study;
  isStudyFiscalYear = true;
  financialAdjFilter: FinancialAdjustmentFilter = new FinancialAdjustmentFilter();
  years: number[] = [];
  financialRatios: TreeNode[] = [];
  selectedFinancialRatios: TreeNode[] = [];
  financialItems: TreeNode[] = [];
  selectedFinancialItems: TreeNode[] = [];

  private ratios: Ratio[] = [];
  private lstfinancialItems: List<VendorAccountMapping> = new List<VendorAccountMapping>();
  private lstRatioVendorAccountMapping: List<RatioVendorAccountMapping> = new List<RatioVendorAccountMapping>();
  private ratioVendorAccountMappingListOrg: List<RatioVendorAccountMapping> = new List<RatioVendorAccountMapping>();

  private arr_vendorAccounts: string[] = [];

  submitted = false;
  blockui = false;
  private _success = new Subject<string>();
  message = '';
  type = 'success';
  loading = false;

  constructor(
    private activeModal: NgbActiveModal,
    private masterMatrixService: MasterMatrixService,
    private lookupService: LookupService,
    private studyDetailService: StudyDetailService,
    private logger: LoggingService,
    private treeviewService: TreeViewService,
    private dialogService: DialogService,
    private studyService: StudyService
  ) {}

  async ngOnInit() {
    this.blockui = true;

    this.exportAll = this.isExportFromSearchstrategy ? this.isExportFromSearchstrategy : this.exportAll;

    this.study = this.studyDetailService.study;
    this.years = this.lookupService.getYearList(10);

    this._success.subscribe((message) => (this.message = message));
    this._success.pipe(debounceTime(5000)).subscribe(() => (this.message = ''));

    await Promise.all([
      this.studyService.GetFinancialRatioByStudyId(this.study.studyId),
      this.lookupService.GetVendorAccountWithCategory(),
      this.lookupService.GetPLIRatioVendorAccountMapping(),
    ]).then((values) => {
      this.loading = true;
      this.ratios = values[0].filter((x) => x.availableForManualAdjustment == true);
      this.lstfinancialItems.AddRange(values[1]);
      this.ratioVendorAccountMappingListOrg.AddRange(values[2]);
      this.updateDefaultPLIBaseVendorAccount();
      this.getFinancialRatios();
      this.getFinancialItems();
      this.loading = false;
    });

    this.blockui = false;
  }

  public close(): void {
    this.activeModal.close(null);
  }

  async exportToExcel_financial() {
    this.blockui = true;
    this.submitted = true;

    let isValid = false;

    if (this.study && this.study.studyId) {
      this.financialAdjFilter.studyId = this.study.studyId;
      this.financialAdjFilter.studySearchStrategyId = this.study.searchStrategies[0].studySearchStrategyId;

      // Just for binding model
      if (this.isStudyFiscalYear) {
        this.financialAdjFilter.startFY = this.study.periodOfStudyFrom;
        this.financialAdjFilter.endFY = this.study.periodOfStudyTo;
      }

      this.financialAdjFilter.ratioIds = [];
      this.selectedFinancialRatios.forEach((val) => {
        this.financialAdjFilter.ratioIds.push(val.data);
      });

      this.financialAdjFilter.vendorAccountStorageNames = [];
      this.selectedFinancialItems.forEach((val) => {
        if (val.leaf) {
          this.financialAdjFilter.vendorAccountStorageNames.push(val.data);
        }
      });

      // CheckValidity for required fields
      if (
        this.isExportFromSearchstrategy &&
        this.financialAdjFilter.startFY > 0 &&
        this.financialAdjFilter.endFY > 0 &&
        this.financialAdjFilter.vendorAccountStorageNames.length > 0
      ) {
        isValid = true;
      } else if (this.financialAdjFilter.startFY > 0 && this.financialAdjFilter.endFY > 0 && this.financialAdjFilter.ratioIds.length > 0) {
        isValid = true;
      }

      if (isValid) {
        this.submitted = false;
        let reportType: Report;
        if (this.isExportFromSearchstrategy) {
          reportType = Report.SearchStrategyExport;
        } else {
          reportType = this.exportAll ? Report.AllFinancialInfo : Report.AcceptedCompaniesFinancialInfo;
        }

        this.masterMatrixService.exportFinancialInformation(this.financialAdjFilter, reportType);

        await this.dialogService.acknowledge('Processing Report ...', AcknowledgeMessage.ReportGenerationStarted, 'OK');

        this.close();
      }
    }

    this.logger.info(this.constructor.name, 'exportToExcel_financial', `financialAdjFilter: ${JSON.stringify(this.financialAdjFilter)}`);

    if (this.isStudyFiscalYear) {
      this.financialAdjFilter.startFY = null;
      this.financialAdjFilter.endFY = null;
    }

    this.blockui = false;
  }

  checkPeriodEnding() {
    if (this.financialAdjFilter.startFY && this.financialAdjFilter.endFY > 0 && this.financialAdjFilter.endFY && this.financialAdjFilter.endFY > 0) {
      return !(this.financialAdjFilter.startFY <= this.financialAdjFilter.endFY);
    }
    return false;
  }

  ShowValidationMessage(item: NgModel): boolean {
    return item.errors && (this.submitted || !item.pristine || item.touched);
  }

  ShowValidationMessageForRatio(): boolean {
    return this.submitted && !(this.selectedFinancialRatios.length > 0);
  }

  getFinancialRatios() {
    const fRatios = [];
    this.ratios.forEach((val) => {
      fRatios.push({
        label: val.name,
        data: val.ratioId,
        leaf: true,
        selectable: true,
      });
    });
    this.financialRatios = fRatios;
  }

  getFinancialItems() {
    const category = this.lstfinancialItems.DistinctBy((a) => a.vendorAccountCategoryId).ToArray();

    category.forEach((val) => {
      // Get child elements
      const childItems = this.lstfinancialItems.Where((a) => a.vendorAccountCategoryId === val.vendorAccountCategoryId).ToList();

      // Bind Parent and Child Data
      this.financialItems.push({
        label: val.vendorAccountCategoryName,
        data: val.vendorAccountCategoryId,
        leaf: childItems.Count() > 0 ? false : true,
        selectable: true,
        children: (function () {
          const t = new List<TreeNode>();
          childItems.ForEach((v) => {
            t.Add({
              label: v.vendorAccountName,
              data: v.vendorAccountStorageName,
              leaf: true,
            });
          });
          return t.ToArray();
        })(),
      });
    });

    return this.financialItems;
  }

  selectFinancialItem(event) {
    if (event.checked) {
      // header checkbox selected

      this.ratios.forEach((val) => {
        this.AddVendorAccount(val);
      });
    } else {
      // single checkbox selected

      if (event.data !== undefined) {
        const ratio: Ratio = this.ratios.find((a) => a.ratioId === event.data.data);

        this.AddVendorAccount(ratio);
      }
    }
    this.treeviewService.checkNode(this.financialItems, this.arr_vendorAccounts, this.selectedFinancialItems);
  }

  async nodeSelect(event) {
    const vendorAccountStorageNames = this.lstfinancialItems
      .Where((a) => a.vendorAccountCategoryId === event.node.data)
      .Select((a) => a.vendorAccountStorageName)
      .ToList();
    vendorAccountStorageNames.ForEach((val) => {
      if (this.arr_vendorAccounts.findIndex((a) => a === val) === -1) {
        this.arr_vendorAccounts.push(val);
      }
    });
    this.treeviewService.checkNode(this.financialItems, this.arr_vendorAccounts, this.selectedFinancialItems);
  }

  async nodeUnselect(event) {
    let ratioData = [];

    // Get ratio data based on financial items.
    if (event.node.leaf) {
      // If children selected
      ratioData = this.ratios.filter(
        (a) =>
          (a.numerator && a.numerator.toLowerCase().trim().indexOf(event.node.data.toLowerCase().trim()) !== -1) ||
          (a.denominator && a.denominator.toLowerCase().trim().indexOf(event.node.data.toLowerCase().trim()) !== -1)
      );
    } else {
      // If parent selected
      const vendorAccountStorageNames = this.lstfinancialItems
        .Where((a) => a.vendorAccountCategoryId === event.node.data)
        .Select((a) => a.vendorAccountStorageName)
        .ToList();

      vendorAccountStorageNames.ForEach((val) => {
        this.ratios
          .filter(
            (a) =>
              (a.numerator && a.numerator.toLowerCase().trim().indexOf(val.toLowerCase().trim()) !== -1) ||
              (a.denominator && a.denominator.toLowerCase().trim().indexOf(val.toLowerCase().trim()) !== -1)
          )
          .forEach((innerVal) => {
            if (ratioData.findIndex((a) => a.ratioId === innerVal.ratioId) === -1) {
              ratioData.push(innerVal);
            }
          });
      });
    }

    let isRatioExist = false;
    if (ratioData.length > 0) {
      isRatioExist = this.selectedFinancialRatios.map((a) => a.data).some((r) => ratioData.map((a) => a.ratioId).indexOf(r) >= 0);
    }

    if (isRatioExist) {
      const confirmationResult = await this.dialogService.confirm('Please Confirm', ConfirmationMessage.FinancialSetting, 'Yes', 'No');
      if (confirmationResult === ConfirmationResult.yes) {
        // remove financial items
        this.treeviewService.removeNode(event.node, this.arr_vendorAccounts);

        ratioData.forEach((val) => {
          const index = this.selectedFinancialRatios.findIndex((a) => a.data === val.ratioId);
          if (index !== -1) {
            // remove ratio
            this.selectedFinancialRatios.splice(index, 1);

            // get remaining ratio
            this.selectedFinancialRatios = this.selectedFinancialRatios.filter((a) => a.data !== val.ratioId);
          }
        });
      }
    } else {
      this.treeviewService.removeNode(event.node, this.arr_vendorAccounts);
    }

    this.treeviewService.checkNodeForUnSelect(this.financialItems, this.arr_vendorAccounts, this.selectedFinancialItems);
  }

  private AddVendorAccount(val: Ratio) {
    const rvm = this.lstRatioVendorAccountMapping.Where((a) => a.ratioId === val.ratioId && a.weightingFactor === false).ToList();

    // If study has weighted factor averaging method then below code will add additional vendor account name that is related to calculation of weighted factor.
    if (this.study.averagingMethodID === AveragingMethodId.WeightedAverage) {
      rvm.AddRange(this.lstRatioVendorAccountMapping.Where((a) => a.ratioId === val.ratioId && a.weightingFactor === true).ToArray());
    }

    if (rvm != null && rvm.Count() > 0) {
      rvm.ForEach((val2) => {
        const vm = this.lstfinancialItems.Where((a) => a.vendorAccountId === val2.vendorAccountId).FirstOrDefault();
        if (vm) {
          if (this.arr_vendorAccounts.findIndex((a) => a === vm.vendorAccountStorageName) === -1) {
            this.arr_vendorAccounts.push(vm.vendorAccountStorageName);
          }
        }
      });
    }
  }

  private updateDefaultPLIBaseVendorAccount() {
    this.lstRatioVendorAccountMapping = _.cloneDeep(this.ratioVendorAccountMappingListOrg);
    this.lstRatioVendorAccountMapping.ForEach((v) =>
      v.vendorAccountId === BasePLIVendorAccount.OperatingRevenue && this.study.defaultBasePLIVendorAccountId != BasePLIVendorAccount.OperatingRevenue
        ? (v.vendorAccountId = this.study.defaultBasePLIVendorAccountId)
        : ''
    );
  }
}
